import { Fragment, useEffect, useState, useMemo } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { InFeedAd, SidebarAd } from "@/components/AdSense";
import SidebarCustomAds from "@/components/SidebarCustomAds";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, User, ArrowRight, ChevronRight, Wrench, Hand, TreePine, Armchair, Search, Award } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { categoryConfig } from "@/lib/categoryConfig";
import { categoryAliases, mainCategoryAliases } from "@/lib/categoryAliases";
import { reviewSections } from "@/lib/reviewSectionConfig";
import { adviceSections } from "@/lib/adviceSectionConfig";
import { productHubs } from "@/lib/productHubConfig";
import { BreadcrumbSchema } from "@/components/SEO";
import { cn } from "@/lib/utils";
import { Star } from "lucide-react";
import reviewsBanner from "@/assets/category-reviews.webp";
import seasonalGuideBanner from "@/assets/category-seasonal-guide.webp";
import lawnMowerBanner from "@/assets/lawn-mower-banner.webp";
import powerToolsBanner from "@/assets/category-power-tools.webp";
import lawnMowersCategoryBanner from "@/assets/category-lawn-mowers.webp";
import chainsawsBanner from "@/assets/category-chainsaws.webp";
import generatorsBanner from "@/assets/generators-banner.png.asset.json";
import { SeasonalAmazonPicks } from "@/components/SeasonalAmazonPicks";
import SsrLinkGrid from "@/components/SsrLinkGrid";
import type { ListingLink } from "@/lib/listingLinks";
import { resolveCategoryRedirect } from "@/lib/categoryRedirects";
import { logNotFound } from "@/lib/logNotFound";


const Category = ({ ssrLinks = [], ssrSubLinks = [] }: { ssrLinks?: ListingLink[]; ssrSubLinks?: ListingLink[] } = {}) => {
  const { categorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const subcategorySlug = searchParams.get("sub");

  // Legacy/alias slugs are redirected server-side (301) in the route's
  // beforeLoad; this is the client-side navigation safety net.
  const redirectTarget = useMemo(
    () => resolveCategoryRedirect(categorySlug, subcategorySlug),
    [categorySlug, subcategorySlug],
  );

  useEffect(() => {
    if (redirectTarget) navigate(redirectTarget, { replace: true });
  }, [redirectTarget, navigate]);

  const config = categorySlug ? categoryConfig[categorySlug] : null;

  // Diagnostics for genuinely unknown category slugs only
  useEffect(() => {
    if (!config && !redirectTarget && categorySlug) {
      void logNotFound("notfound", `/category/${categorySlug}`);
    }
  }, [config, redirectTarget, categorySlug]);

  // Fetch category info
  const { data: category } = useQuery({
    queryKey: ['category', categorySlug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('slug', categorySlug!)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!categorySlug,
  });

  // Fetch subcategories
  const { data: subcategories } = useQuery({
    queryKey: ['subcategories', categorySlug],
    queryFn: async () => {
      if (!config) return [];
      const slugs = config.sections.flatMap(s => s.links.map(l => l.slug));
      
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .in('slug', slugs);

      if (error) throw error;
      return data;
    },
    enabled: !!config,
  });

  // Fetch articles for this category or subcategory with flexible matching
  const { data: articles, isLoading: articlesLoading } = useQuery({
    queryKey: ['category-articles', categorySlug, subcategorySlug],
    queryFn: async () => {
      if (!config || !categorySlug) return [];

      // Build list of all category names to match
      let categoriesToMatch: string[] = [];

      if (subcategorySlug) {
        // Get subcategory info
        const subcat = config.sections.flatMap(s => s.links).find(l => l.slug === subcategorySlug);
        if (subcat) {
          // Add subcategory name and all its aliases
          categoriesToMatch.push(subcat.name);
          const aliases = categoryAliases[subcategorySlug] || [];
          categoriesToMatch.push(...aliases);
        }
      } else {
        // Main category view - get all subcategory names and their aliases
        categoriesToMatch.push(config.title);
        
        // Add main category aliases
        const mainAliases = mainCategoryAliases[categorySlug] || [];
        categoriesToMatch.push(...mainAliases);
        
        // Add all subcategory names and their aliases
        for (const section of config.sections) {
          for (const link of section.links) {
            categoriesToMatch.push(link.name);
            const aliases = categoryAliases[link.slug] || [];
            categoriesToMatch.push(...aliases);
          }
        }
      }

      // Deduplicate category names
      categoriesToMatch = [...new Set(categoriesToMatch)];

      // If we somehow have no categories to match, avoid returning everything.
      if (categoriesToMatch.length === 0) return [];

      // PostgREST filter helpers (used in `.or()` below)
      const escapePgOrValue = (value: string) => {
        // Quote + escape to safely support spaces/special chars.
        const escaped = value.replace(/\\/g, "\\\\").replace(/\"/g, "\\\"");
        return `\"${escaped}\"`;
      };

      const pgInList = categoriesToMatch.map(escapePgOrValue).join(",");
      const pgArrayList = categoriesToMatch.map(escapePgOrValue).join(",");

      const { data, error } = await supabase
        .from('articles')
        .select('id, slug, title, excerpt, featured_image, featured_image_alt, category, categories, author, published_at, product_rating, tags')
        .eq('status', 'published')
        // Match either legacy single-category OR new multi-category array
        .or(`category.in.(${pgInList}),categories.ov.{${pgArrayList}}`)
        .order('published_at', { ascending: false })
        .limit(12);

      if (error) throw error;
      return data;
    },
    enabled: !!categorySlug && !!config,
  });

  // Map subcategory slugs to product database tables and URL prefixes
  // `powerColumn` is the column that feeds the "power" filter — generators
  // store this as fuel_type, so it is aliased to power_type in the select.
  const productTableMap: Record<string, { table: string; urlPrefix: string; guidePath: string; powerColumn: string }> = {
    'lawn-mowers': { table: 'lawn_mowers', urlPrefix: '/lawn-mowers', guidePath: '/best-lawn-mowers-uk', powerColumn: 'power_type' },
    'chainsaws': { table: 'chainsaws', urlPrefix: '/chainsaws', guidePath: '/best-chainsaws-uk', powerColumn: 'power_type' },
    'generators': { table: 'generators', urlPrefix: '/generators', guidePath: '/best-generators-uk', powerColumn: 'fuel_type' },
    'pressure-washers': { table: 'pressure_washers', urlPrefix: '/pressure-washers', guidePath: '/best-pressure-washers-uk', powerColumn: 'power_type' },
  };

  const productTableInfo = subcategorySlug ? productTableMap[subcategorySlug] : null;

  // Fetch published products for subcategory (fetch more for filtering)
  const { data: categoryProducts } = useQuery({
    queryKey: ['category-products', subcategorySlug],
    queryFn: async () => {
      if (!productTableInfo) return [];
      const { data, error } = await supabase
        .from(productTableInfo.table as any)
        .select(`id, slug, brand, model, image_url, price_gbp, rating, best_for, power_type:${productTableInfo.powerColumn}`)
        .eq('status', 'published')
        .order('rating', { ascending: false, nullsFirst: false })
        .limit(50);
      if (error) throw error;
      return (data as any[]) || [];
    },
    enabled: !!productTableInfo,
  });

  // Product filter state
  const [productBrandFilter, setProductBrandFilter] = useState<string | null>(null);
  const [productPowerFilter, setProductPowerFilter] = useState<string | null>(null);
  const [productSearchQuery, setProductSearchQuery] = useState("");

  // Reset filters when subcategory changes
  useEffect(() => {
    setProductBrandFilter(null);
    setProductPowerFilter(null);
    setProductSearchQuery("");
  }, [subcategorySlug]);

  // Derive unique brands and power types from products
  const productFilterOptions = useMemo(() => {
    if (!categoryProducts) return { brands: [], powerTypes: [] };
    const brands = [...new Set(categoryProducts.map((p: any) => p.brand))].sort();
    const powerTypes = [...new Set(categoryProducts.map((p: any) => p.power_type).filter(Boolean))].sort();
    return { brands, powerTypes };
  }, [categoryProducts]);

  // Filter products
  const filteredProducts = useMemo(() => {
    if (!categoryProducts) return [];
    return categoryProducts.filter((p: any) => {
      if (productBrandFilter && p.brand !== productBrandFilter) return false;
      if (productPowerFilter && p.power_type !== productPowerFilter) return false;
      if (productSearchQuery) {
        const q = productSearchQuery.toLowerCase();
        const text = `${p.brand} ${p.model} ${p.best_for || ''}`.toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    });
  }, [categoryProducts, productBrandFilter, productPowerFilter, productSearchQuery]);

  // Keyword map for matching product landings to categories/subcategories
  const categoryKeywords: Record<string, string[]> = {
    // Subcategory-level keywords
    'lawn-mowers': ['mower', 'lawn'],
    'chainsaws': ['chainsaw', 'chain-saw', 'log-splitter', 'log splitter', 'pruner'],
    'generators': ['generator'],
    'pressure-washers': ['pressure-washer', 'pressure washer', 'washer', 'jet-wash'],
    'cement-mixers': ['cement', 'mixer', 'concrete', 'mortar', 'belle'],
    'wood-chippers': ['wood-chipper', 'wood chipper', 'chipper', 'shredder', 'shredders'],
    'power-tools': ['multi-tool', 'blower', 'trimmer', 'hedge', 'strimmer'],
    // Main category-level keywords
    'reviews': ['mower', 'lawn', 'chainsaw', 'generator', 'pressure', 'washer', 'blower', 'trimmer', 'hedge', 'strimmer', 'tool', 'garden'],
    'how-to': ['guide', 'how', 'tips', 'best'],
    'grow-guides': ['grow', 'plant', 'garden', 'seed', 'vegetable', 'herb', 'fruit'],
    'plants': ['plant', 'shrub', 'tree', 'flower', 'perennial', 'annual', 'bulb'],
    'flowers': ['flower', 'rose', 'dahlia', 'tulip', 'sunflower', 'bloom'],
    'garden-design': ['design', 'garden', 'patio', 'path', 'lighting', 'raised bed'],
    'seasonal-guide': ['spring', 'summer', 'autumn', 'winter', 'season', 'month', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'],
  };

  // Fetch published product landings for any category page
  const { data: productLandings } = useQuery({
    queryKey: ['category-product-landings', categorySlug, subcategorySlug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_landings')
        .select('id, slug, title, subtitle, hero_image, rating, review_count, products')
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!categorySlug,
  });

  // Filter product landings relevant to current category or subcategory
  const relevantLandings = (() => {
    if (!productLandings || !categorySlug) return [];
    const matchKey = subcategorySlug || categorySlug;
    const keywords = categoryKeywords[matchKey] || [matchKey.replace(/-/g, ' ')];
    return productLandings.filter(landing => {
      const searchText = `${landing.title} ${landing.slug}`.toLowerCase();
      return keywords.some(kw => searchText.includes(kw.toLowerCase()));
    });
  })();

  // Map subcategory slugs to top_lists product_type values
  const subcatToProductType: Record<string, string> = {
    'lawn-mowers': 'lawn_mowers',
    'chainsaws': 'chainsaws',
    'generators': 'generators',
    'pressure-washers': 'pressure_washers',
  };

  // Determine which product types to fetch top lists for
  const topListProductTypes = useMemo(() => {
    if (subcategorySlug && subcatToProductType[subcategorySlug]) {
      return [subcatToProductType[subcategorySlug]];
    }
    if (categorySlug === 'reviews') {
      return Object.values(subcatToProductType);
    }
    return [];
  }, [categorySlug, subcategorySlug]);

  // Fetch published top lists for relevant product types
  const { data: topLists } = useQuery({
    queryKey: ['category-top-lists', topListProductTypes],
    queryFn: async () => {
      if (topListProductTypes.length === 0) return [];
      const { data, error } = await supabase
        .from('top_lists')
        .select('id, slug, title, featured_image, featured_image_alt, product_type, items, published_at, author')
        .eq('status', 'published')
        .in('product_type', topListProductTypes)
        .order('published_at', { ascending: false })
        .limit(6);
      if (error) throw error;
      return data || [];
    },
    enabled: topListProductTypes.length > 0,
  });

  // Fetch featured articles (most recent 3)
  const featuredArticles = articles?.slice(0, 3) || [];
  const remainingArticles = articles?.slice(3) || [];

  const estimateReadTime = (content?: string | null) => {
    if (!content) return "5 min read";
    const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const minutes = Math.ceil(wordCount / 200);
    return `${minutes} min read`;
  };

  // A redirect is in flight — never render the 404 copy (this was being counted
  // as a real "Category Not Found" pageview for every alias/legacy URL).
  if (redirectTarget) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, follow" />
          <title>Redirecting… | Garden-Review</title>
          <link rel="canonical" href={`https://www.garden-review.co.uk${redirectTarget.split("?")[0]}`} />
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="container mx-auto px-4 lg:px-8 py-24 text-center">
            <p className="text-muted-foreground">Taking you to the right page…</p>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  if (!config) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, follow" />
          <meta name="prerender-status-code" content="404" />
          <title>Category Not Found | Garden-Review</title>
          <meta name="description" content="This category doesn't exist. Browse Garden-Review's reviews, buying guides and growing advice instead." />
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="container mx-auto px-4 lg:px-8 py-12">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-2xl font-bold mb-3">Category Not Found</h1>
              <p className="text-muted-foreground mb-8">
                We couldn't find that category, but these sections should help you find what you're after.
              </p>
            </div>
            <div className="max-w-3xl mx-auto grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10">
              {Object.entries(categoryConfig).map(([slug, cfg]) => (
                <Link
                  key={slug}
                  to={`/category/${slug}`}
                  className="rounded-md border bg-card px-4 py-3 text-sm font-medium hover:border-primary hover:text-primary transition-colors"
                >
                  {cfg.title}
                </Link>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/"><Button>Back to Home</Button></Link>
              <Link to="/articles"><Button variant="outline">Browse Articles</Button></Link>
              <Link to="/browse"><Button variant="outline">All Pages</Button></Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const activeSubcategory = subcategorySlug 
    ? config.sections.flatMap(s => s.links).find(l => l.slug === subcategorySlug)
    : null;

  const pageTitle = activeSubcategory 
    ? `${activeSubcategory.name} | ${config.title}` 
    : config.title;

  const pageDescription = activeSubcategory
    ? `Explore our ${activeSubcategory.name.toLowerCase()} guides and articles`
    : category?.description || config.description;

  const heroBannerImage = 
    (categorySlug === "reviews" && subcategorySlug === "lawn-mowers") ? lawnMowersCategoryBanner :
    (categorySlug === "reviews" && subcategorySlug === "power-tools") ? powerToolsBanner :
    (categorySlug === "reviews" && subcategorySlug === "chainsaws") ? chainsawsBanner :
    (categorySlug === "reviews" && subcategorySlug === "generators") ? generatorsBanner.url :
    categorySlug === "wood-chippers" ? powerToolsBanner :
    categorySlug === "reviews" ? reviewsBanner : 
    categorySlug === "seasonal-guide" ? seasonalGuideBanner : 
    null;
  const hasHeroBanner = Boolean(heroBannerImage);

  // Build breadcrumb items for schema
  const breadcrumbItems: Array<{ name: string; url?: string }> = [
    { name: "Home", url: "/" }
  ];
  
  if (activeSubcategory) {
    breadcrumbItems.push({ name: config.title, url: `/category/${categorySlug}` });
    breadcrumbItems.push({ name: activeSubcategory.name });
  } else {
    breadcrumbItems.push({ name: config.title });
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Title, description, canonical, OG and BreadcrumbList are rendered
          server-side by the route head(); only the conditional robots tag stays here. */}
      {(subcategorySlug || (!articlesLoading && (articles?.length ?? 0) < 3)) && (
        <Helmet>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
      )}
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className={`relative overflow-hidden py-2 md:py-2.5 ${config.heroGradient}`}>
          {heroBannerImage && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${heroBannerImage})` }}
              aria-hidden="true"
            />
          )}
          {heroBannerImage && (
            <div
              className="absolute inset-0 bg-linear-to-b from-background/85 via-background/65 to-background/35"
              aria-hidden="true"
            />
          )}
          <div className="relative z-10 container mx-auto px-4 lg:px-8">
            <div
              className={
                hasHeroBanner
                  ? "flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4 rounded-xl bg-background/75 backdrop-blur-sm px-4 py-2"
                  : "flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4 py-1"
              }
            >
              {/* Breadcrumb */}
              <nav
                className={
                  hasHeroBanner
                    ? "flex items-center gap-2 text-xs text-foreground/80 min-w-0"
                    : "flex items-center gap-2 text-xs text-muted-foreground min-w-0"
                }
              >
                <Link to="/" className="hover:text-foreground transition-colors whitespace-nowrap">Home</Link>
                <ChevronRight className="h-3 w-3 shrink-0" />
                {activeSubcategory ? (
                  <>
                    <Link to={`/category/${categorySlug}`} className="hover:text-foreground transition-colors whitespace-nowrap">
                      {config.title}
                    </Link>
                    <ChevronRight className="h-3 w-3 shrink-0" />
                    <span className="text-foreground font-medium whitespace-nowrap">{activeSubcategory.name}</span>
                  </>
                ) : (
                  <span className="text-foreground font-medium whitespace-nowrap">{config.title}</span>
                )}
              </nav>

              {/* Title + description (right-aligned, inline with breadcrumb) */}
              <div className="flex items-center gap-2.5 md:justify-end min-w-0">
                <div className={`p-1 rounded-lg ${config.iconBg} shrink-0`}>
                  <config.icon className={`h-4 w-4 ${config.iconColor}`} />
                </div>
                <h1 className="text-lg md:text-xl font-bold text-foreground whitespace-nowrap leading-tight">
                  {activeSubcategory ? activeSubcategory.name : config.title}
                </h1>
                <span className="hidden md:block text-foreground/30" aria-hidden="true">·</span>
                <p className="hidden md:block text-sm text-foreground/75 line-clamp-1 max-w-md">
                  {pageDescription}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Subcategory Sub-Menu (inline nav style) */}
        <section className="py-4 border-b border-border bg-background">
          <div className="container mx-auto px-4 lg:px-8">
            <nav className="flex flex-wrap items-center gap-x-1 gap-y-2">
              {categorySlug === "reviews" ? (
                <>
                  <Link
                    to="/category/reviews"
                    className={cn(
                      "px-3 py-2 text-sm font-medium rounded-md transition-colors",
                      !activeSubcategory ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    )}
                  >
                    All Reviews
                  </Link>
                  {(() => {
                    // Map review section titles to their landing-page slugs
                    const sectionSlugMap: Record<string, string> = {
                      "Power Tools": "power-tools",
                      "Garden Machinery": "garden-machinery",
                      "Hand Tools": "hand-tools",
                      "Garden Kit": "garden-kit",
                      "Outdoor Furniture": "outdoor-furniture",
                      "By Brand": "brands",
                    };
                    // Determine which section the current ?sub= belongs to
                    const activeSection = (() => {
                      if (!subcategorySlug) return null;
                      for (const section of config.sections) {
                        if (section.links.some(l => l.slug === subcategorySlug)) {
                          return section.title;
                        }
                      }
                      return null;
                    })();
                    return config.sections.map(section => {
                      const sectionSlug = sectionSlugMap[section.title];
                      const isActive = activeSection === section.title;
                      if (!sectionSlug) return null;
                      return (
                        <Link
                          key={section.title}
                          to={`/reviews/${sectionSlug}`}
                          className={cn(
                            "px-3 py-2 text-sm font-medium rounded-md transition-colors",
                            isActive
                              ? "text-primary bg-primary/10"
                              : "text-muted-foreground hover:text-foreground hover:bg-muted"
                          )}
                        >
                          {section.title}
                        </Link>
                      );
                    });
                  })()}
                </>
              ) : (
                <>
                  <Link
                    to={`/category/${categorySlug}`}
                    className={cn(
                      "px-3 py-2 text-sm font-medium rounded-md transition-colors",
                      !activeSubcategory ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    )}
                  >
                    All {config.title}
                  </Link>
                  {config.sections.flatMap(s => s.links).map((link) => (
                    <Link
                      key={link.slug}
                      to={link.href ? link.href : `/category/${categorySlug}?sub=${link.slug}`}
                      className={cn(
                        "px-3 py-2 text-sm font-medium rounded-md transition-colors",
                        link.slug === subcategorySlug
                          ? "text-primary bg-primary/10"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      )}
                    >
                      {link.name}
                    </Link>
                  ))}
                </>
              )}
            </nav>
          </div>
        </section>

        {categorySlug === "reviews" && subcategorySlug === "lawn-mowers" && (
          <section className="py-8">
            <div className="container mx-auto px-4 lg:px-8">
              <Link to="/best-lawn-mowers-uk" className="block group">
                <div className="relative overflow-hidden rounded-2xl bg-linear-to-r from-primary/90 via-primary to-primary/80">
                  <div
                    className="absolute inset-0 bg-cover bg-center opacity-30 group-hover:opacity-40 transition-opacity duration-500"
                    style={{ backgroundImage: `url(${lawnMowerBanner})` }}
                    aria-hidden="true"
                  />
                  <div className="absolute inset-0 bg-linear-to-r from-primary/70 via-transparent to-primary/50" aria-hidden="true" />
                  <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 p-8 md:p-10">
                    <div className="text-center md:text-left">
                      <Badge className="mb-3 bg-white/20 text-white border-white/30 hover:bg-white/30">
                        2026 Guide
                      </Badge>
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                        Best Lawn Mowers UK 2026
                      </h3>
                      <p className="text-white/85 text-lg max-w-lg">
                        Find the perfect mower for your garden with our interactive buying guide &amp; expert reviews.
                      </p>
                    </div>
                    <Button size="lg" variant="secondary" className="shrink-0 text-base font-semibold group-hover:scale-105 transition-transform">
                      View Guide
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </Link>
            </div>
          </section>
        )}

        {/* Promotional Banner for Chainsaw Guide */}
        {categorySlug === "reviews" && subcategorySlug === "chainsaws" && (
          <section className="py-8">
            <div className="container mx-auto px-4 lg:px-8">
              <Link to="/best-chainsaws-uk" className="block group">
                <div className="relative overflow-hidden rounded-2xl bg-linear-to-r from-primary/90 via-primary to-primary/80">
                  <div className="absolute inset-0 bg-linear-to-r from-primary/70 via-transparent to-primary/50" aria-hidden="true" />
                  <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 p-8 md:p-10">
                    <div className="text-center md:text-left">
                      <Badge className="mb-3 bg-white/20 text-white border-white/30 hover:bg-white/30">
                        2026 Guide
                      </Badge>
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                        Best Chainsaws UK 2026
                      </h3>
                      <p className="text-white/85 text-lg max-w-lg">
                        Find the perfect chainsaw with our interactive buying guide &amp; expert comparisons.
                      </p>
                    </div>
                    <Button size="lg" variant="secondary" className="shrink-0 text-base font-semibold group-hover:scale-105 transition-transform">
                      View Guide
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </Link>
            </div>
          </section>
        )}

        {/* Promotional Banner for Pressure Washer Guide */}
        {categorySlug === "reviews" && subcategorySlug === "pressure-washers" && (
          <section className="py-8">
            <div className="container mx-auto px-4 lg:px-8">
              <Link to="/best-pressure-washers-uk" className="block group">
                <div className="relative overflow-hidden rounded-2xl bg-linear-to-r from-primary/90 via-primary to-primary/80">
                  <div className="absolute inset-0 bg-linear-to-r from-primary/70 via-transparent to-primary/50" aria-hidden="true" />
                  <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 p-8 md:p-10">
                    <div className="text-center md:text-left">
                      <Badge className="mb-3 bg-white/20 text-white border-white/30 hover:bg-white/30">
                        2026 Guide
                      </Badge>
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                        Best Pressure Washers UK 2026
                      </h3>
                      <p className="text-white/85 text-lg max-w-lg">
                        Find the perfect pressure washer with our interactive buying guide &amp; expert comparisons.
                      </p>
                    </div>
                    <Button size="lg" variant="secondary" className="shrink-0 text-base font-semibold group-hover:scale-105 transition-transform">
                      View Guide
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </Link>
            </div>
          </section>
        )}

        {/* Promotional Banner for Generator Guide */}
        {categorySlug === "reviews" && subcategorySlug === "generators" && (
          <section className="py-8">
            <div className="container mx-auto px-4 lg:px-8">
              <Link to="/best-generators-uk" className="block group">
                <div className="relative overflow-hidden rounded-2xl bg-linear-to-r from-primary/90 via-primary to-primary/80">
                  <div className="absolute inset-0 bg-linear-to-r from-primary/70 via-transparent to-primary/50" aria-hidden="true" />
                  <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 p-8 md:p-10">
                    <div className="text-center md:text-left">
                      <Badge className="mb-3 bg-white/20 text-white border-white/30 hover:bg-white/30">
                        2026 Guide
                      </Badge>
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                        Best Generators UK 2026
                      </h3>
                      <p className="text-white/85 text-lg max-w-lg">
                        Find the perfect generator with our interactive buying guide &amp; expert comparisons.
                      </p>
                    </div>
                    <Button size="lg" variant="secondary" className="shrink-0 text-base font-semibold group-hover:scale-105 transition-transform">
                      View Guide
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </Link>
            </div>
          </section>
        )}



        {relevantLandings.length > 0 && (
          <section className="py-12 border-b border-border">
            <div className="container mx-auto px-4 lg:px-8">
              <h2 className="text-2xl font-bold mb-2">Featured Product Pages</h2>
              <p className="text-muted-foreground mb-8">In-depth reviews and buying guides for top products</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relevantLandings.slice(0, 6).map((landing) => {
                  const products = (landing.products as unknown as Array<{ title: string; image: string }>) || [];
                  const heroImage = landing.hero_image || products[0]?.image;
                  return (
                    <Link key={landing.id} to={`/product/${landing.slug}`}>
                      <Card className="overflow-hidden rounded-xl shadow-soft hover:shadow-nature transition-shadow duration-300 h-full group border-border/50">
                        {heroImage && (
                          <div className="relative h-48 overflow-hidden bg-muted">
                            <img
                              src={heroImage}
                              alt={landing.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            {landing.rating && (
                              <div className="absolute top-3 right-3 bg-background/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1">
                                <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                                <span className="text-sm font-medium">{landing.rating}</span>
                              </div>
                            )}
                          </div>
                        )}
                        <CardContent className="pt-4">
                          <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-1">
                            {landing.title}
                          </h3>
                          {landing.subtitle && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{landing.subtitle}</p>
                          )}
                          <div className="flex items-center text-sm text-primary font-medium">
                            View Details
                            <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Top Lists / Best-Of Guides */}
        {topLists && topLists.length > 0 && (
          <section className="py-12 border-b border-border">
            <div className="container mx-auto px-4 lg:px-8">
              <h2 className="text-2xl font-bold mb-2">Best-Of Guides</h2>
              <p className="text-muted-foreground mb-8">Curated top picks to help you choose the right product</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {topLists.map((list) => {
                  const itemCount = Array.isArray(list.items) ? list.items.length : 0;
                  return (
                    <Link key={list.id} to={`/best/${list.slug}`}>
                      <Card className="overflow-hidden rounded-xl shadow-soft hover:shadow-nature transition-shadow duration-300 h-full group border-border/50">
                        {list.featured_image && (
                          <div className="relative h-48 overflow-hidden bg-muted">
                            <img
                              src={list.featured_image}
                              alt={list.featured_image_alt || list.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                            />
                            <div className="absolute top-3 left-3">
                              <Badge className="bg-primary text-primary-foreground">
                                <Award className="h-3 w-3 mr-1" />
                                Top {itemCount}
                              </Badge>
                            </div>
                          </div>
                        )}
                        <CardContent className="pt-4">
                          <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
                            {list.title}
                          </h3>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            {list.author && <span>By {list.author}</span>}
                            {list.published_at && (
                              <span>• {new Date(list.published_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}</span>
                            )}
                          </div>
                          <div className="flex items-center text-sm text-primary font-medium mt-3">
                            Read Guide
                            <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Featured Articles */}
        {featuredArticles.length > 0 && (
          <section className="py-12">
            <div className="container mx-auto px-4 lg:px-8">
              <h2 className="text-2xl font-bold mb-8">
                {activeSubcategory ? `Latest in ${activeSubcategory.name}` : 'Featured Articles'}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Featured */}
                <Link to={`/articles/${featuredArticles[0].slug}`} className="lg:col-span-2">
                  <Card className="overflow-hidden rounded-xl shadow-soft hover:shadow-nature transition-shadow duration-300 h-full">
                    {featuredArticles[0].featured_image && (
                      <div className="relative h-64 lg:h-80 overflow-hidden">
                        <img
                          src={featuredArticles[0].featured_image}
                          alt={featuredArticles[0].title}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-linear-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                          {featuredArticles[0].category && (
                            <Badge className="mb-3 bg-primary">{featuredArticles[0].category}</Badge>
                          )}
                          <h3 className="text-2xl lg:text-3xl font-bold mb-2">{featuredArticles[0].title}</h3>
                          {featuredArticles[0].excerpt && (
                            <p className="text-white/80 line-clamp-2">{featuredArticles[0].excerpt}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </Card>
                </Link>

                {/* Secondary Featured */}
                <div className="space-y-4">
                  {featuredArticles.slice(1, 3).map((article) => (
                    <Link key={article.id} to={`/articles/${article.slug}`}>
                      <Card className="overflow-hidden rounded-xl shadow-soft hover:shadow-nature transition-shadow duration-300">
                        <div className="flex">
                          {article.featured_image && (
                            <div className="relative w-32 h-32 shrink-0 overflow-hidden">
                              <img
                                src={article.featured_image}
                                alt={article.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="p-4 flex-1">
                            {article.category && (
                              <Badge variant="secondary" className="mb-2 text-xs">{article.category}</Badge>
                            )}
                            <h4 className="font-semibold text-foreground line-clamp-2 hover:text-primary transition-colors">
                              {article.title}
                            </h4>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Father's Day gift guide promo — June only */}
        {categorySlug === "seasonal-guide" && (subcategorySlug || "").toLowerCase() === "june" && (
          <section className="py-6">
            <div className="container mx-auto px-4 lg:px-8">
              <Link to="/gifts/fathers-day" className="block group">
                <Card className="overflow-hidden border-primary/20 hover:shadow-xl transition-all duration-300">
                  <div className="grid md:grid-cols-[280px_1fr] items-stretch">
                    <div className="relative h-48 md:h-auto overflow-hidden bg-muted">
                      <img
                        src="/__l5e/assets-v1/59225b1d-5442-4655-b329-a135127cb12c/fathers-day-banner.jpg"
                        alt="Father's Day garden gift guide — top-rated UK gardening gifts for dad"
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6 md:p-8 flex flex-col justify-center">
                      <Badge variant="secondary" className="w-fit mb-3">June Highlight</Badge>
                      <h3 className="text-2xl md:text-3xl font-bold mb-2 group-hover:text-primary transition-colors">
                        Father's Day Garden Gift Guide
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        Top-rated UK gardening gifts for dad under £50, £100, £300 and £500 — hand-picked from our independent reviews.
                      </p>
                      <span className="text-primary font-semibold">Browse the gift guide →</span>
                    </div>
                  </div>
                </Card>
              </Link>
            </div>
          </section>
        )}

        {/* Seasonal Amazon picks — auto-populated from PA-API (after Latest in <Month>) */}
        {categorySlug === "seasonal-guide" && (
          <section className="py-4">
            <div className="container mx-auto px-4 lg:px-8">
              <SeasonalAmazonPicks
                month={["january","february","march","april","may","june","july","august","september","october","november","december"].includes((subcategorySlug || "").toLowerCase()) ? (subcategorySlug as string) : undefined}
              />
            </div>
          </section>
        )}

        {/* All Articles Grid */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 lg:px-8">
            <h2 className="text-2xl font-bold mb-8">
              {activeSubcategory ? 'More Articles' : 'Latest Articles'}
            </h2>
            
            {articlesLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <Card key={i} className="overflow-hidden">
                    <Skeleton className="h-48 w-full" />
                    <CardHeader>
                      <Skeleton className="h-4 w-24 mb-2" />
                      <Skeleton className="h-6 w-full" />
                    </CardHeader>
                    <CardContent>
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-3/4" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : remainingArticles.length === 0 && featuredArticles.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg mb-4">
                  No articles in this category yet.
                </p>
                <p className="text-sm text-muted-foreground">
                  Check back soon for new content!
                </p>
              </div>
            ) : (
              <div className="flex gap-8">
                {/* Main Content */}
                <div className="flex-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {remainingArticles.slice(0, 6).map((article, index) => (
                      <Fragment key={article.id}>
                        <Link to={`/articles/${article.slug}`}>
                          <Card className="overflow-hidden rounded-xl shadow-soft hover:shadow-nature transition-shadow duration-300 border-border/50 h-full bg-background">
                            {article.featured_image && (
                              <div className="relative h-48 overflow-hidden">
                                <img
                                  src={article.featured_image}
                                  alt={article.title}
                                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                                />
                              </div>
                            )}
                            <CardHeader className="pb-2">
                              <div className="flex items-center gap-3 text-sm text-muted-foreground mb-2">
                                {article.category && (
                                  <Badge variant="secondary">{article.category}</Badge>
                                )}
                                <div className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  <span>{estimateReadTime()}</span>
                                </div>
                              </div>
                              <h3 className="text-xl font-bold text-foreground leading-tight hover:text-primary transition-colors">
                                {article.title}
                              </h3>
                            </CardHeader>
                            <CardContent>
                              {article.excerpt && (
                                <p className="text-muted-foreground mb-4 line-clamp-3">
                                  {article.excerpt}
                                </p>
                              )}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  {article.author && (
                                    <>
                                      <User className="h-3 w-3" />
                                      <span>{article.author}</span>
                                    </>
                                  )}
                                  {article.published_at && (
                                    <>
                                      <span>•</span>
                                      <span>{format(new Date(article.published_at), 'MMM d, yyyy')}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                        {/* Show ad after every 6 articles */}
                        {(index + 1) % 6 === 0 && index < remainingArticles.length - 1 && (
                          <div className="col-span-1 md:col-span-2 lg:col-span-3">
                            <InFeedAd />
                          </div>
                        )}
                      </Fragment>
                    ))}
                  </div>
                </div>
                
                {/* Sidebar */}
                <aside className="hidden lg:block w-72 xl:w-80 shrink-0">
                  <div className="sticky top-24 space-y-6">
                    <SidebarCustomAds />
                    <SidebarAd />
                  </div>
                </aside>

              </div>
            )}

            {remainingArticles.length > 6 && (
              <div className="text-center mt-12">
                <Link to={`/articles?category=${categorySlug}`}>
                  <Button size="lg">
                    See More Articles
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Product Review Pages */}
        {categoryProducts && categoryProducts.length > 0 && productTableInfo && (
          <section className="py-12 border-b border-border">
            <div className="container mx-auto px-4 lg:px-8">
              <h2 className="text-2xl font-bold mb-2">Product Reviews</h2>
              <p className="text-muted-foreground mb-6">In-depth reviews and specifications for individual products</p>

              {/* Filter Bar */}
              <div className="flex flex-wrap items-center gap-3 mb-8 p-4 bg-muted/50 rounded-xl border border-border/50">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-hidden focus:ring-2 focus:ring-primary/30"
                  />
                </div>

                {/* Brand Filter */}
                {productFilterOptions.brands.length > 1 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground mr-1">Brand:</span>
                    <Badge
                      variant={!productBrandFilter ? "default" : "outline"}
                      className="cursor-pointer text-xs"
                      onClick={() => setProductBrandFilter(null)}
                    >
                      All
                    </Badge>
                    {productFilterOptions.brands.map((brand: string) => (
                      <Badge
                        key={brand}
                        variant={productBrandFilter === brand ? "default" : "outline"}
                        className="cursor-pointer text-xs hover:bg-primary hover:text-primary-foreground transition-colors"
                        onClick={() => setProductBrandFilter(productBrandFilter === brand ? null : brand)}
                      >
                        {brand}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Power Type Filter */}
                {productFilterOptions.powerTypes.length > 1 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground mr-1">Power:</span>
                    <Badge
                      variant={!productPowerFilter ? "default" : "outline"}
                      className="cursor-pointer text-xs"
                      onClick={() => setProductPowerFilter(null)}
                    >
                      All
                    </Badge>
                    {productFilterOptions.powerTypes.map((pt: string) => (
                      <Badge
                        key={pt}
                        variant={productPowerFilter === pt ? "default" : "outline"}
                        className="cursor-pointer text-xs capitalize hover:bg-primary hover:text-primary-foreground transition-colors"
                        onClick={() => setProductPowerFilter(productPowerFilter === pt ? null : pt)}
                      >
                        {pt.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {filteredProducts.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No products match your filters.</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredProducts.slice(0, 8).map((product: any) => (
                      <Link key={product.id} to={`${productTableInfo.urlPrefix}/${product.slug}`}>
                        <Card className="overflow-hidden rounded-xl shadow-soft hover:shadow-nature transition-shadow duration-300 h-full group border-border/50">
                          {product.image_url && (
                            <div className="relative h-48 overflow-hidden bg-muted">
                              <img
                                src={product.image_url}
                                alt={`${product.brand} ${product.model}`}
                                className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                              />
                              {product.rating && (
                                <div className="absolute top-3 right-3 bg-background/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1">
                                  <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                                  <span className="text-sm font-medium">{product.rating}</span>
                                </div>
                              )}
                            </div>
                          )}
                          <CardContent className="pt-4">
                            <Badge variant="secondary" className="mb-2 text-xs">{product.brand}</Badge>
                            <h3 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-1">
                              {product.brand} {product.model}
                            </h3>
                            {product.best_for && (
                              <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{product.best_for}</p>
                            )}
                            <div className="flex items-center justify-between">
                              {product.price_gbp && (
                                <span className="text-sm font-bold text-foreground">£{Number(product.price_gbp).toFixed(2)}</span>
                              )}
                              <span className="text-xs text-primary font-medium flex items-center">
                                View Review <ArrowRight className="ml-1 h-3 w-3" />
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                  {filteredProducts.length > 8 && (
                    <div className="text-center mt-10">
                      <Link to={productTableInfo.guidePath}>
                        <Button size="lg">
                          See More Reviews
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        )}
      </main>

      <SsrLinkGrid title="Explore this section" links={ssrSubLinks} />
      <SsrLinkGrid
        title="Articles in this category"
        links={ssrLinks}
      />
      <Footer />
    </div>
  );
};

export default Category;
